import styles from '../../bmweDataTable.module.css';
import { CSSProperties, ReactNode } from 'react';
import { Column } from '@tanstack/react-table';
import { Skeleton } from 'primereact/skeleton';
import { BmweColumnMeta } from '../../../../types';

// Varies bar widths across rows and columns so the skeleton doesn't look striped.
const BAR_WIDTHS = ['70%', '45%', '60%', '35%', '55%'];

interface SkeletonRowProps<TRow> {
  columns: Column<TRow, unknown>[];
  virtualStart: number;
  virtualIndex: number;
  ariaRowIndex: number;
  measure: (el: Element | null) => void;
  loadingSkeletonRow?: ReactNode;
}

export function SkeletonRow<TRow>({
  columns,
  virtualStart,
  virtualIndex,
  ariaRowIndex,
  measure,
  loadingSkeletonRow,
}: SkeletonRowProps<TRow>) {
  return (
    <tr
      key={`skeleton-${virtualIndex}`}
      data-index={virtualIndex}
      ref={(node) => measure(node)}
      role="row"
      aria-rowindex={ariaRowIndex}
      className={styles.bmweDataTableRow}
      style={{ '--rowTranslateY': `${virtualStart}px` } as CSSProperties}
      aria-busy="true"
    >
      {loadingSkeletonRow ? (
        <td
          role="gridcell"
          tabIndex={-1}
          className={`${styles.bmweDataTableCell} ${styles.tableCell} ${styles.skeletonCell}`}
          aria-hidden="true"
        >
          {loadingSkeletonRow}
        </td>
      ) : (
        columns.map((column, columnIndex) => {
          const meta = column.columnDef.meta as BmweColumnMeta | undefined;
          return (
            <td
              key={column.id}
              role="gridcell"
              tabIndex={-1}
              className={`${styles.bmweDataTableCell} ${styles.tableCell}`}
              style={
                {
                  '--columnJustify': meta?.justify ?? 'start',
                } as CSSProperties
              }
              aria-hidden="true"
            >
              <Skeleton
                width={
                  BAR_WIDTHS[(virtualIndex + columnIndex) % BAR_WIDTHS.length]
                }
                height="1rem"
              />
            </td>
          );
        })
      )}
    </tr>
  );
}
